import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import styled from "styled-components";
import { colors } from "../../../../Shared/colors";
import { useGetUnifiedTasksByLeadQuery } from "../../../../features/unifiedTaskSlice";
import { decryptData } from "../../../../services/crypto";
import Tooltip from "@mui/material/Tooltip";

export const UnifiedTaskTable = ({ leadID, leadNumber, clientPhone, onClose }) => {
  const { data, isLoading, isFetching } = useGetUnifiedTasksByLeadQuery(
    { leadID },
    { skip: !leadID, refetchOnMountOrArgChange: true }
  );
  const role = decryptData(localStorage.getItem("role"));
  const rows = useMemo(() => {
    const tasks = data?.tasks || [];
    return tasks.map((t, idx) => ({
      id: t.id || idx,
      type: t.type,
      clientPhone: clientPhone || "",
      taskType: t.taskType,
      taskSubType: t.taskSubType,
      comment: t.comment,
      attachment: t.attachment || "",
      empName: t.empName || t?.performedBy?.name || "",
      completedDate: t.completedDate,
      taskStatus: t.taskStatus || "",
    }));
  }, [data, role]);

  const columns = [
    {
      field: "clientPhone", headerName: "Client Number", flex: 0.8, minWidth: 130,
      renderCell: (params) => (
        <Tooltip title={String(params.value || "")} placement="top" arrow>
          <span className="cell-ellipsis">{params.value}</span>
        </Tooltip>
      )
    },
    { 
      field: "taskType", headerName: "Task", flex: 0.9, minWidth: 110,
      renderCell: (params) => (
        <Tooltip title={String(params.value || "")} placement="top" arrow>
          <span className="cell-ellipsis">{params.value}</span>
        </Tooltip>
      )
    },
    { 
      field: "taskSubType", headerName: "Sub Type", flex: 1, minWidth: 160,
      renderCell: (params) => (
        <Tooltip title={String(params.value || "")} placement="top" arrow>
          <span className="cell-ellipsis">{params.value}</span>
        </Tooltip>
      )
    },
    { 
      field: "comment", headerName: "Comment", flex: 1.4, minWidth: 220,
      renderCell: (params) => (
        <Tooltip title={String(params.value || "")} placement="top" arrow>
          <span className="cell-ellipsis">{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: "empName",
      headerName: "Performed By",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const row = params.row;
        // TS role: keep employee identity masked.
        if (role === "ts") {
          if (row.type === "telesales_task") {
            return (
              <Tooltip title="Telesales user" placement="top" arrow>
                <RoleBadge color="#1a6b6b">You (TS)</RoleBadge>
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Original employee" placement="top" arrow>
              <RoleBadge color="#6b7280">Employee</RoleBadge>
            </Tooltip>
          );
        }

        // Non-TS role: show employee performer name, but keep TS identity generic.
        if (row.type === "telesales_task") {
          return (
            <Tooltip title="Telesales" placement="top" arrow>
              <RoleBadge color="#2563eb">Telesales</RoleBadge>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={row.empName || "Employee"} placement="top" arrow>
            <span className="cell-ellipsis">{row.empName || "Employee"}</span>
          </Tooltip>
        );
      }
    },
    { 
      field: "completedDate", headerName: "Completed At", flex: 1, minWidth: 180,
      renderCell: (params) => {
        const val = params.value ? new Date(params.value).toLocaleString() : "";
        return (
          <Tooltip title={val} placement="top" arrow>
            <span className="cell-ellipsis">{val}</span>
          </Tooltip>
        );
      }
    },
    {
      field: "attachment",
      headerName: "Attachment",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const url = params.value;
        if (!url) return <span style={{ color: '#9ca3af' }}>None</span>;
        const isImage = /(\.png|\.jpg|\.jpeg|\.gif|\.webp)$/i.test(url);
        return (
          <Tooltip title={<span>Open attachment</span>} placement="top" arrow>
            <a href={url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {isImage ? (
                <img src={url} alt="attachment" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
              ) : (
                <span className="cell-link">View</span>
              )}
            </a>
          </Tooltip>
        );
      }
    },
  ];

  return (
    <Container>
      <Header>
        <div className="title" />
        {data?.assignment && (
          <AssignmentBar>
            <span className="chip status">{data.assignment.status}</span>
            <span className="chip">Assigned: {new Date(data.assignment.assignmentDate).toLocaleString()}</span>
            {data.assignment.lastActivityDate && (
              <span className="chip">Last Activity: {new Date(data.assignment.lastActivityDate).toLocaleString()}</span>
            )}
            <span className="chip muted">LeadID: {leadNumber || ""}</span>
          </AssignmentBar>
        )}
      </Header>
      <GridWrap>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          density="compact"
          loading={isLoading || isFetching}
          disableSelectionOnClick
          sx={{
            border: "1px solid #e0e0e0",
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f8f9fa' },
          }}
        />
      </GridWrap>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  .title { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding-right: 56px; }
  h3 { margin: 0; color: ${colors.textBlue}; font-size: 1.1rem; }
  .badges { display: flex; gap: 8px; flex-wrap: wrap; }
`;

// removed badges per latest spec

const AssignmentBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  .chip { background: ${colors.lightWhite}; border: 1px solid ${colors.inputBorderWhite}; color: ${colors.text}; padding: 4px 8px; border-radius: 8px; font-size: 0.75rem; }
  .chip.status { text-transform: capitalize; background: #eef9f7; border-color: #bfe7e1; color: #0f766e; }
  .chip.muted { color: ${colors.textMed}; }
`;

const GridWrap = styled.div`
  width: 100%;
  height: 60vh;
  min-height: 360px;
  @media (max-width: 768px) {
    height: 55vh;
  }
`;

const RoleBadge = styled.span`
  background: ${(p) => p.color || '#6b7280'};
  color: white;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
`;


